import { LocationWindow } from '../LocationWindow'
import { useState } from 'react'
import type { God } from '@mezha/api'
import { useChooseGodMutation, useFaithQuery } from '../../api/endpoints/social'
import { useHeroQuery } from '../../api/endpoints/hero'
import { useCommand } from '../../lib/useCommand'
import { clockText, countdown, useRefetchAt, useServerNow } from '../../lib/time'
import { useShellMode } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { ConfirmInPlace, PlankButton, cx } from '../../ui'
import { modList } from '../common'
import './faith.css'

/** `/play/faith`: patrons with their modifiers; the first choice is free, a change costs and waits. */
export function Faith() {
  useShellMode('window')
  const faith = useFaithQuery()
  const hero = useHeroQuery()
  const now = useServerNow(1000)
  const [choose] = useChooseGodMutation()
  const cmd = useCommand(() => void faith.refetch())
  const [picked, setPicked] = useState<string | null>(null)
  const f = faith.data
  useRefetchAt(f?.ready_at, faith.refetch)
  const chosen = f?.gods.find(g => g.id === (picked ?? f.current)) ?? f?.gods[0] ?? null
  const wait = f?.ready_at && Date.parse(f.ready_at) > now ? countdown(now, f.ready_at) : null
  const first = !f?.current
  const inCombat = hero.data?.in_combat

  const dispatch = useAppDispatch()
  const canChoose = Boolean(chosen && f && chosen.id !== f.current && !wait && !inCombat)
  const act = async (g: God) => {
    const r = await cmd.run(() => choose({ god_id: g.id }).unwrap())
    if (r) dispatch(ui.actions.notice(`Покровитель: ${g.name}`))
  }

  return (
    <LocationWindow title="Боги" emblem="nav.gods" idBase="faith" bodyClassName="faith">
      {faith.isLoading && <Loading />}
      {faith.isError && !f && <Failure error={faith.error} retry={() => void faith.refetch()} what="Боги" />}
      {f && f.gods.length === 0 && <Empty>Покровители пока не явились.</Empty>}
      {f && f.gods.length > 0 && (
        <>
          <ul className="faith__gods" role="radiogroup" aria-label="Покровители">
            {f.gods.map(g => (
              <li key={g.id}>
                <button type="button" role="radio" aria-checked={chosen?.id === g.id} className={cx('mode', chosen?.id === g.id && 'is-selected')} onClick={() => setPicked(g.id)}>
                  {g.emblem ? <img className="faith__emblem" src={g.emblem} alt="" /> : <Emblem name="nav.gods" />}
                  <span><b>{g.name}</b><small>{g.id === f.current ? 'ваш покровитель' : modList(g.modifiers).slice(0, 2).map(m => `${m.label} ${m.text}`).join(', ')}</small></span>
                </button>
              </li>
            ))}
          </ul>
          {chosen && (
            <div className="modecard">
              <div className="parch">
                {chosen.emblem && <img className="faith__crest" src={chosen.emblem} alt="" />}
                <h2>{chosen.name}</h2>
                <p className="faith__about">{chosen.description}</p>
                <div className="mods">{modList(chosen.modifiers).map(m => <Pair key={m.key} a={m.label} b={m.text} />)}</div>
              </div>
              <p className="note">
                {first ? 'Первый выбор бесплатный.' : `Смена покровителя стоит ${f.change_price} серебра${hero.data ? ` (у вас ${hero.data.silver})` : ''}.`}
                {wait && f.ready_at && ` Сменить можно будет в ${clockText(f.ready_at)} (через ${wait.text}).`}
                {inCombat && ' Во время боя выбор закрыт.'}
              </p>
              {chosen.id !== f.current && (
                <ConfirmInPlace key={chosen.id} yes={first ? 'Да, выбрать' : 'Да, сменить'} onConfirm={() => act(chosen)}
                  question={first ? <>{chosen.name} станет вашим покровителем. Это бесплатно.</> : <>{chosen.name} заменит нынешнего покровителя за <b>{f.change_price}</b> серебра{hero.data ? `; у вас ${hero.data.silver}` : ''}.</>}>
                  {ask => (
                    <div className="actions">
                      <PlankButton gold locked={!canChoose} onClick={ask}>{first ? `Выбрать: ${chosen.name}` : `Сменить · ${f.change_price} серебра`}</PlankButton>
                    </div>
                  )}
                </ConfirmInPlace>
              )}
              {cmd.text && <p className="faith__error" role="alert">{cmd.text}</p>}
            </div>
          )}
        </>
      )}
    </LocationWindow>
  )
}

function Pair({ a, b }: { a: string; b: string }) {
  return <><span>{a}</span><u>{b}</u></>
}
